import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Plus, 
  Search, 
  Loader2, 
  Briefcase,
  MoreHorizontal,
  Pencil,
  Clock,
  AlertTriangle,
  Filter,
  Trash2,
  ArrowUp,
  ArrowDown,
  CalendarClock,
} from 'lucide-react';
import { cn, parseScheduledDate } from '@/lib/utils';

// Import extracted components
import { 
  AddOrderDialog, 
  EditOrderDialog, 
  OrderDetailSheet,
  OrderScheduleDialog,
  CreateServiceDialog,
  OPERATIONAL_STATUSES,
  PAYMENT_STATUSES
} from '@/components/orders';

// Re-export AddOrderDialog for external use (e.g., CalendarPage)
export { AddOrderDialog } from '@/components/orders';

// Quick filter presets
const QUICK_FILTERS = [
  { id: 'needs_payment', label: 'Нужна оплата', op: 'done', pay: 'unpaid' },
  { id: 'new_orders', label: 'Новые', op: 'new', pay: '' },
  { id: 'in_progress', label: 'В работе', op: 'in_progress', pay: '' },
  { id: 'done_today', label: 'Выполнено', op: 'done', pay: '' },
  { id: 'cancelled', label: 'Отменённые', op: 'cancelled', pay: '' },
  { id: 'deleted', label: 'Удалённые', op: 'deleted', pay: '' },
  { id: 'all', label: 'Все', op: '', pay: '' },
  { id: 'remote_zone', label: 'Зона 50+', op: 'new', pay: '', remote: true },
  { id: 'service', label: 'Сервис', op: '', pay: '', service: true },
];

// Main Orders Page
export default function OrdersPage() {
  const { api, hasRole } = useAuth();
  const { t, lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Default: "Done & Unpaid"
  const [statusFilter, setStatusFilter] = useState('done');
  const [paymentFilter, setPaymentFilter] = useState('unpaid');
  const [activeQuickFilter, setActiveQuickFilter] = useState('needs_payment');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDialogOrder, setDeleteDialogOrder] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  // Scheduling
  const [scheduleOrder, setScheduleOrder] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // Remote zone
  const [remoteCount, setRemoteCount] = useState(0);
  const [remoteFilter, setRemoteFilter] = useState(false);
  // Service
  const [serviceCount, setServiceCount] = useState(0);
  const [serviceFilter, setServiceFilter] = useState(false);
  const [serviceOrder, setServiceOrder] = useState(null);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);

  // Check for preselected client from URL
  const preselectedClientId = searchParams.get('client_id');
  const filterParam = searchParams.get('filter');

  // Apply URL filter param on mount
  useEffect(() => {
    if (filterParam) {
      const preset = QUICK_FILTERS.find(f => f.id === filterParam);
      if (preset) {
        setActiveQuickFilter(preset.id);
        setStatusFilter(preset.op);
        setPaymentFilter(preset.pay);
        setRemoteFilter(!!preset.remote);
        setServiceFilter(!!preset.service);
      }
    }
  }, [filterParam]);

  const fetchOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (statusFilter) params.append('op_status', statusFilter);
      if (paymentFilter) params.append('pay_status', paymentFilter);
      if (remoteFilter) params.append('is_remote', 'true');
      if (serviceFilter) params.append('order_type', 'service');
      params.append('sort_by', 'date');
      params.append('sort_order', sortOrder);
      
      const response = await api.get(`/orders?${params.toString()}`);
      setOrders(response.data);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      toast.error(t('orders.loadFailed') || 'Не удалось загрузить заказы');
    } finally {
      setLoading(false);
    }
  }, [api, searchQuery, statusFilter, paymentFilter, remoteFilter, serviceFilter, sortOrder]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchOrders();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchOrders]);

  // Open dialog with preselected client
  useEffect(() => {
    if (preselectedClientId) {
      setDialogOpen(true);
    }
  }, [preselectedClientId]);

  // Fetch remote + service counts
  useEffect(() => {
    Promise.all([
      api.get('/orders/remote-stats').catch(() => ({ data: { unassigned_remote: 0 } })),
      api.get('/orders/service-stats').catch(() => ({ data: { active_services: 0 } })),
    ]).then(([remoteRes, serviceRes]) => {
      setRemoteCount(remoteRes.data.unassigned_remote || 0);
      setServiceCount(serviceRes.data.active_services || 0);
    });
  }, [api, orders]);

  const handleView = (order) => {
    setViewOrder(order);
    setDetailOpen(true);
  };

  const handleEdit = (order) => {
    setEditOrder(order);
    setEditDialogOpen(true);
  };

  const handleSchedule = (order) => {
    setScheduleOrder(order);
    setScheduleOpen(true);
  };

  const handleCreateService = (order) => {
    setServiceOrder(order);
    setServiceDialogOpen(true);
  };

  const handleViewService = (serviceOrder) => {
    setViewOrder(serviceOrder);
    setDetailOpen(true);
  };

  const handleQuickFilter = (filter) => {
    setActiveQuickFilter(filter.id);
    setStatusFilter(filter.op);
    setPaymentFilter(filter.pay);
    setRemoteFilter(!!filter.remote);
    setServiceFilter(!!filter.service);
  };

  const handleDelete = (order) => {
    if (order.operational_status === 'done') {
      toast.error(t('orders.cannotDeleteCompleted'));
      return;
    }
    if (order.operational_status === 'deleted') {
      toast.error('Заказ уже удалён');
      return;
    }
    setDeleteDialogOrder(order);
    setDeleteReason('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialogOrder || !deleteReason.trim()) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/orders/${deleteDialogOrder.id}`, { data: { reason: deleteReason.trim() } });
      toast.success(t('orders.deleted'));
      setDeleteDialogOrder(null);
      setDeleteReason('');
      fetchOrders();
    } catch (err) {
      console.error('Failed to delete order:', err);
      toast.error(err.response?.data?.detail || t('orders.deleteFailed'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const canManage = hasRole('admin') || hasRole('manager');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('orders.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('orders.subtitle')}
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setDialogOpen(true)} data-testid="add-order-btn">
            <Plus className="mr-2 h-4 w-4" />
            {t('orders.newOrder')}
          </Button>
        )}
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2" data-testid="quick-filters">
        {QUICK_FILTERS.map(f => (
          <Button
            key={f.id}
            variant={activeQuickFilter === f.id ? "default" : "outline"}
            size="sm"
            className={cn(
              "text-xs h-8 relative",
              activeQuickFilter === f.id && !f.remote && !f.service && "bg-primary",
              f.remote && activeQuickFilter === f.id && "bg-orange-500 hover:bg-orange-600 text-white border-orange-500",
              f.remote && activeQuickFilter !== f.id && "border-orange-500 text-orange-600 bg-orange-50 hover:bg-orange-100 hover:border-orange-600 font-semibold",
              f.service && activeQuickFilter === f.id && "bg-red-500 hover:bg-red-600 text-white border-red-500",
              f.service && activeQuickFilter !== f.id && "border-red-500 text-red-600 bg-red-50 hover:bg-red-100 hover:border-red-600 font-semibold",
            )}
            onClick={() => handleQuickFilter(f)}
            data-testid={`quick-filter-${f.id}`}
          >
            {f.label}
            {f.remote && remoteCount > 0 && (
              <span className={cn(
                "ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold px-1",
                activeQuickFilter === f.id
                  ? "bg-white text-primary"
                  : "bg-orange-500 text-white"
              )} data-testid="remote-count-badge">
                {remoteCount}
              </span>
            )}
            {f.service && serviceCount > 0 && (
              <span className={cn(
                "ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold px-1",
                activeQuickFilter === f.id
                  ? "bg-white text-primary"
                  : "bg-amber-500 text-white"
              )} data-testid="service-count-badge">
                {serviceCount}
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* Advanced Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('orders.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="orders-search"
          />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => {
          setStatusFilter(v === "all" ? "" : v);
          setActiveQuickFilter('');
          setRemoteFilter(false);
          setServiceFilter(false);
        }}>
          <SelectTrigger className="w-[180px]" data-testid="orders-status-filter">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder={t('orders.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('orders.allStatuses')}</SelectItem>
            {Object.entries(OPERATIONAL_STATUSES).map(([key, { labelKey }]) => (
              <SelectItem key={key} value={key}>{t(labelKey)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={paymentFilter || "all"} onValueChange={(v) => {
          setPaymentFilter(v === "all" ? "" : v);
          setActiveQuickFilter('');
          setRemoteFilter(false);
          setServiceFilter(false);
        }}>
          <SelectTrigger className="w-[180px]" data-testid="orders-payment-filter">
            <SelectValue placeholder={t('orders.allPayments')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('orders.allPayments')}</SelectItem>
            {Object.entries(PAYMENT_STATUSES).map(([key, { labelKey }]) => (
              <SelectItem key={key} value={key} data-testid={`payment-filter-${key}`}>{t(labelKey)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders List */}
      <Card className="cgc-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{searchQuery || statusFilter || paymentFilter ? t('orders.notFound') : t('orders.noOrders')}</p>
              {canManage && !searchQuery && !statusFilter && !paymentFilter && (
                <Button 
                  variant="link" 
                  onClick={() => setDialogOpen(true)}
                  className="mt-2"
                >
                  {t('orders.newOrder')}
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[300px]">{t('orders.title')}</TableHead>
                  <TableHead>{t('orders.client')}</TableHead>
                  <TableHead>
                    <button
                      onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                      className="inline-flex items-center gap-2 font-medium hover:text-accent transition-colors cursor-pointer"
                      data-testid="sort-by-date"
                    >
                      {t('orders.date')}
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-accent/20 border border-accent/30">
                        {sortOrder === 'asc'
                          ? <ArrowUp className="h-4 w-4 text-accent" />
                          : <ArrowDown className="h-4 w-4 text-accent" />
                        }
                      </span>
                    </button>
                  </TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead>{t('orders.workers')}</TableHead>
                  <TableHead className="text-right">{t('orders.total')}</TableHead>
                  <TableHead className="text-right" data-testid="orders-paid-header">{t('orders.paid')}</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => {
                  const opStatus = OPERATIONAL_STATUSES[order.operational_status];
                  const payStatus = PAYMENT_STATUSES[order.payment_status];
                  const StatusIcon = opStatus?.icon || Clock;
                  
                  return (
                    <TableRow 
                      key={order.id} 
                      data-testid={`order-row-${order.id}`}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleView(order)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            {order.order_type === 'service' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-600 border border-amber-500/30">Сервис</span>
                            )}
                            {order.title}
                          </p>
                          {order.service_type_name && (
                            <p className="text-sm text-muted-foreground">
                              {order.service_type_name}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.client?.name}</p>
                          {order.client?.phone && (
                            <p className="text-sm text-muted-foreground">{order.client.phone}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {order.scheduled_date ? (
                          <div>
                            <p className="font-medium">
                              {parseScheduledDate(order.scheduled_date)?.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', { day: 'numeric', month: 'short' })}
                            </p>
                            {order.scheduled_time_start && (
                              <p className="text-sm text-muted-foreground">{order.scheduled_time_start}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge className={cn("w-fit flex items-center gap-1", opStatus?.color)}>
                            <StatusIcon className="h-3 w-3" />
                            {t(opStatus?.labelKey)}
                          </Badge>
                          <Badge className={cn("w-fit", payStatus?.color)}>
                            {t(payStatus?.labelKey)}
                          </Badge>
                          {order.operational_status === 'deleted' && order.deleted_by && (
                            <p className="text-[10px] text-gray-500 mt-0.5">
                              {order.deleted_by}{order.deletion_reason ? `: ${order.deletion_reason}` : ''}
                            </p>
                          )}
                          {order.operational_status === 'cancelled' && order.cancelled_by && (
                            <p className="text-[10px] text-red-500 mt-0.5">
                              {order.cancelled_by}{order.cancelled_reason ? `: ${order.cancelled_reason}` : ''}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {order.assigned_workers && order.assigned_workers.length > 0 ? (
                          <div className="flex -space-x-2">
                            {order.assigned_workers.slice(0, 3).map(worker => (
                              <Avatar key={worker.id} className="h-7 w-7 border-2 border-background">
                                <AvatarFallback 
                                  style={{ backgroundColor: worker.color }}
                                  className="text-white text-xs"
                                >
                                  {worker.first_name?.[0]}{worker.last_name?.[0]}
                                </AvatarFallback>
                              </Avatar>
                            ))}
                            {order.assigned_workers.length > 3 && (
                              <div className="h-7 w-7 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs">
                                +{order.assigned_workers.length - 3}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">{t('orders.notAssigned')}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-medium tabular-nums">
                          {t('currency.format', order.total_amount || 0)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right" data-testid={`order-paid-${order.id}`}>
                        <span className="tabular-nums text-muted-foreground">
                          {t('currency.format', order.amount_paid || 0)}
                        </span>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`order-menu-${order.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleView(order)} data-testid="order-menu-view">
                              <Briefcase className="mr-2 h-4 w-4" />
                              Открыть
                            </DropdownMenuItem>
                            {canManage && (
                              <>
                                {order.operational_status === 'new' && (
                                  <DropdownMenuItem 
                                    onClick={() => handleSchedule(order)} 
                                    data-testid="order-menu-schedule"
                                    className="text-primary focus:text-primary"
                                  >
                                    <CalendarClock className="mr-2 h-4 w-4" />
                                    Запланировать
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleEdit(order)} data-testid="order-menu-edit">
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Редактировать
                                </DropdownMenuItem>
                                {!['done', 'deleted'].includes(order.operational_status) && (
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(order)}
                                    className="text-destructive focus:text-destructive"
                                    data-testid="order-menu-delete"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Удалить
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Order Dialog */}
      <AddOrderDialog 
        open={dialogOpen} 
        onOpenChange={setDialogOpen}
        onSuccess={fetchOrders}
        preselectedClient={preselectedClientId ? { id: preselectedClientId } : null}
      />

      {/* Order Detail Sheet */}
      <OrderDetailSheet
        order={viewOrder}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onRefresh={fetchOrders}
        onSchedule={handleSchedule}
        onCreateService={handleCreateService}
        onViewService={(svc) => {
          setViewOrder(svc);
          setDetailOpen(true);
        }}
      />

      {/* Edit Order Dialog */}
      <EditOrderDialog
        order={editOrder}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={fetchOrders}
      />

      {/* Schedule Order Dialog */}
      <OrderScheduleDialog
        order={scheduleOrder}
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        onScheduled={fetchOrders}
      />

      {/* Create Service Dialog */}
      <CreateServiceDialog
        order={serviceOrder}
        open={serviceDialogOpen}
        onOpenChange={setServiceDialogOpen}
        onCreated={fetchOrders}
      />

      {/* Delete Order Dialog with Reason */}
      {deleteDialogOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteDialogOrder(null)}>
          <div className="bg-background rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Удалить заказ</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {deleteDialogOrder.title} — {deleteDialogOrder.client?.name}
            </p>
            <label className="text-sm font-medium">Причина удаления *</label>
            <Textarea
              value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
              placeholder="Укажите причину удаления заказа..."
              rows={3}
              className="mt-1 mb-4"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOrder(null)}>Отмена</Button>
              <Button
                variant="destructive"
                disabled={!deleteReason.trim() || deleteLoading}
                onClick={handleConfirmDelete}
              >
                {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Удалить
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
